import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useRef } from 'react';
import { type ConversationScope, GuardianContext } from '../../components/GuardianProvider';
import type { ChatRequestSourceEnum, ConversationMetadata } from '../../gen/openapi';
import { type McpTool, type McpToolResult, useMcpBridge } from '../../mcp/McpBridgeProvider';
import type { ClientChatResponse } from '../../types/ClientMessage';
import { logError } from '../../utils/logger';
import { useApiClient } from '../useApiClient';
import { type Settled, type UiTask, useBackgroundTasks } from '../useBackgroundTasks';
import { useChatUrlInstanceIds } from '../useChatUrlInstanceIds';
import { useSource } from '../useSource';
import { getChatErrorMessage } from './chatErrorMessage';
import { newMessage, toApiMessage } from './chatMessages';
import { messagesKey } from './conversationKeys';

/**
 * Background tasks, chat side.
 *
 * Guardian owns each task's id, polls the host `getTaskStatus` @5 s (see
 * `useBackgroundTasks`), and on a terminal outcome (or the 3 h cap) posts a NEW user
 * turn — never a 2nd tool_result for the same tool_use id, which would 400 — so the LLM
 * writes the completion answer. The note carries FACTS only; the backend AIOps system
 * prompt owns the phrasing. It rides as a hidden 'background_handoff' entry: sent to the
 * backend for context, filtered out of the visible feed.
 */

// "1m 3s" / "12s" — human duration for the background-task hand-off note.
const formatDuration = (ms: number): string => {
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

// The hidden user turn that wakes the LLM when a background task settles. Facts
// only — HOW to phrase the reply is owned by the backend AIOps system prompt.
const backgroundHandoffNote = (d: Settled): string => {
  const took = formatDuration(d.durationMs);
  if (d.outcome === 'completed')
    return `[background task finished] "${d.description}" completed in ${took}. Result: ${d.statusMessage ?? 'done'}.`;
  if (d.outcome === 'timeout')
    return `[background task timed out] "${d.description}" did not finish after 3 hours (${took}).`;
  // d.error is `unknown` — an object would interpolate as "[object Object]", giving the
  // LLM no usable context, so serialize a non-string error before it reaches the note.
  const errorText = typeof d.error === 'string' ? d.error : d.error != null ? JSON.stringify(d.error) : undefined;
  return `[background task failed] "${d.description}" failed after ${took}. Error: ${d.statusMessage ?? errorText ?? 'unknown error'}.`;
};

// Request context captured when a background task is registered, so its completion
// hand-off targets the originating conversation with the params it started with.
type HandoffContext = {
  conversationId: string;
  /** The originating conversation's scope — a dashboard analysis lives under its own cache
   * root, so the hand-off has to write where that conversation actually reads. */
  scope: ConversationScope;
  instanceIds: string[];
  agentSelected?: string;
  mcpTools: McpTool[];
  source?: ChatRequestSourceEnum;
  platformVersion: string | null;
};

/**
 * A tool result carrying `{ taskId, status: 'working' }` means a long-running op just
 * started (an LLM-initiated call — poll responses never reach the chat loop, the bridge
 * resolves those). Returns the id to track, or null for any other result.
 */
export const readBackgroundTaskStart = (result: unknown): { taskId: string; statusMessage?: string } | null => {
  const body = (result as McpToolResult | undefined)?.structuredContent as
    | { taskId?: string; status?: string; statusMessage?: string }
    | undefined;
  if (!body?.taskId || body.status !== 'working') return null;
  return { taskId: body.taskId, statusMessage: body.statusMessage };
};

export type BackgroundTaskHandoff = {
  /** Active tasks, for <BackgroundTasksBar>. */
  tasks: UiTask[];
  /** Start tracking a task id, snapshotting the current request context for its hand-off. */
  registerBackgroundTask: (taskId: string, description: string) => void;
};

export const useBackgroundTaskHandoff = (): BackgroundTaskHandoff => {
  const { conversationId, conversationScope, selectedAgent } = useContext(GuardianContext);
  const { mcpTools, platformVersion } = useMcpBridge();
  const queryClient = useQueryClient();
  const { chat: chatApi } = useApiClient();
  const { selectedInstanceIds } = useChatUrlInstanceIds();
  const source = useSource();

  // Request context snapshotted at the MOMENT a task is registered. The bridge subscriber
  // that calls `registerBackgroundTask` is registered once, and a task can settle after the
  // user switches conversations — so reading any of these at settle time would use the
  // current render's values, not the ones the task STARTED with. Kept in a ref (refreshed
  // each render) and threaded through the task, so the completion POST always carries the
  // originating conversation's context.
  const handoffContextRef = useRef<HandoffContext | null>(null);
  handoffContextRef.current = {
    conversationId,
    scope: conversationScope,
    instanceIds: selectedInstanceIds,
    agentSelected: selectedAgent?.id ?? undefined,
    mcpTools: mcpTools ?? [],
    source: source as ChatRequestSourceEnum | undefined,
    platformVersion,
  };

  const onBackgroundTaskSettled = useCallback(
    (d: Settled, ctx: HandoffContext) => {
      // Post the completion to the conversation that STARTED the task, using the
      // request context captured at registration (ctx) — the user may have switched
      // away. chatMutation is bound to the current conversationId, so post directly.
      const convId = ctx.conversationId;
      // The originating conversation's OWN key: a dashboard analysis reads from
      // ['dashboardChat', id], so a hardcoded ['chat', id] would write where nothing looks.
      const key = messagesKey(ctx.scope, convId);
      const note = backgroundHandoffNote(d);
      const noteMsg: ClientChatResponse = { ...newMessage(note, 'user'), mcpKind: 'background_handoff' };
      // Functional updater (not read-then-write) so a concurrent cache write between
      // the read and the write is not silently dropped; then read back the live cache.
      queryClient.setQueryData<ClientChatResponse[]>(key, (prev) => [...(prev ?? []), noteMsg]);
      const messages = queryClient.getQueryData<ClientChatResponse[]>(key) ?? [];
      const metadata = queryClient.getQueryData<ConversationMetadata | null>(['chatMetadata', convId]) ?? null;
      chatApi
        .chatApiV1ChatPost({
          chatRequest: {
            messages: messages.map(toApiMessage),
            conversationId: convId,
            uiComponentVersion: 1,
            instanceIds: ctx.instanceIds,
            conversationMetadata: metadata,
            agentSelected: ctx.agentSelected,
            mcpTools: ctx.mcpTools,
            source: ctx.source,
            platformVersion: ctx.platformVersion,
          },
        })
        .then((response) => {
          queryClient.setQueryData<ClientChatResponse[]>(key, (prev) => [...(prev ?? messages), response]);
          queryClient.setQueryData<ConversationMetadata | null>(
            ['chatMetadata', convId],
            response.conversationMetadata ?? null,
          );
        })
        .catch(async (error) => {
          logError(error, 'background handoff post failed');
          const message = await getChatErrorMessage(error);
          queryClient.setQueryData<ClientChatResponse[]>(key, (prev) => [
            ...(prev ?? messages),
            newMessage(message, 'assistant', true),
          ]);
        });
    },
    [queryClient, chatApi],
  );

  const { tasks, register } = useBackgroundTasks(onBackgroundTaskSettled);

  // Callers only supply the id + description; the context snapshot is this hook's business.
  const registerBackgroundTask = useCallback(
    (taskId: string, description: string) => {
      const ctx = handoffContextRef.current;
      if (ctx) register(taskId, description, ctx);
    },
    [register],
  );

  return { tasks, registerBackgroundTask };
};
